import React from 'react';
import { Box, Card, CardContent, Avatar, Fade, Typography } from '@mui/material';
import { DESIGN_TOKENS } from '../../../design-system/tokens/colors';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Pets } from '@mui/icons-material';

interface DeathData {
  animal?: {
    id?: string;
    name?: string;
    breed?: string;
    sex?: string;
    birth_date?: string;
    current_weight?: number;
  };
  death_date?: string;
  cause?: string;
  location?: string;
  responsible?: string;
  observations?: string;
  status?: string;
}

interface DeathFeedbackViewProps {
  data?: DeathData | any;
}

// Função para extrair informações do resumo
const extractDeathInfo = (resumoText: string) => {
  // Padrão: "Animal 002, óbito cadastrado com sucesso."
  const match = resumoText.match(/Animal ([^,]+),/);
  if (match) {
    return {
      animalName: match[1],
    };
  }
  return { animalName: 'Animal' };
};

const formatDate = (dateString: string | undefined): string => {
  if (!dateString) return '—';
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '—';
    
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch {
    return '—';
  }
};

export const DeathFeedbackView: React.FC<DeathFeedbackViewProps> = ({ data }) => {
  const { t } = useTranslation();

  if (!data) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">
          Dados do óbito não encontrados
        </Typography>
      </Box>
    );
  }

  const parsed: any = data || {};
  const resumoStr: string = parsed?.resumo || `Animal, óbito cadastrado com sucesso.`;
  
  // Extrair informações do resumo
  const { animalName } = extractDeathInfo(resumoStr);

  return (
    <Fade in timeout={800}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          padding: 3,
          background: '#ffffff',
        }}
      >
        {/* Ícone principal animado */}
        <Box
          sx={{
            position: 'relative',
            marginBottom: 3,
            animation: 'pulse 2s infinite',
            '@keyframes pulse': {
              '0%': {
                transform: 'scale(1)',
                boxShadow: `0 0 0 0 ${DESIGN_TOKENS.colors.primary[200]}40`,
              },
              '70%': {
                transform: 'scale(1.05)',
                boxShadow: `0 0 0 20px ${DESIGN_TOKENS.colors.primary[200]}00`,
              },
              '100%': {
                transform: 'scale(1)',
                boxShadow: `0 0 0 0 ${DESIGN_TOKENS.colors.primary[200]}00`,
              },
            },
          }}
        >
          <Avatar
            sx={{
              width: 80,
              height: 80,
              backgroundColor: DESIGN_TOKENS.colors.primary[100],
              border: `3px solid ${DESIGN_TOKENS.colors.primary[200]}`,
              boxShadow: `0 8px 32px ${DESIGN_TOKENS.colors.primary[200]}40`,
            }}
          >
            <img 
               src="/vaca.svg" 
               alt="Vaca" 
               style={{ 
                 width: 40, 
                 height: 40,
                 filter: `brightness(0) saturate(100%) invert(20%) sepia(77%) saturate(1200%) hue-rotate(180deg) brightness(95%) contrast(101%)`
               }} 
             />
          </Avatar>
          
          {/* Ícone de sucesso sobreposto */}
          <Avatar
            sx={{
              width: 32,
              height: 32,
              backgroundColor: DESIGN_TOKENS.colors.primary[600],
              position: 'absolute',
              bottom: -4,
              right: -4,
              border: `2px solid ${DESIGN_TOKENS.colors.background.paper}`,
            }}
          >
            <CheckCircle
              sx={{
                fontSize: 20,
                color: DESIGN_TOKENS.colors.background.paper,
              }}
            />
          </Avatar>
        </Box>

        {/* Card principal */}
        <Card
          sx={{
            maxWidth: 400,
            width: '100%',
            borderRadius: 3,
            boxShadow: `0 12px 40px ${DESIGN_TOKENS.colors.primary[200]}30`,
            border: `1px solid ${DESIGN_TOKENS.colors.primary[100]}`,
            overflow: 'visible',
          }}
        >
          <CardContent sx={{ padding: 4, textAlign: 'center' }}>
            {/* Título principal */}
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: DESIGN_TOKENS.colors.text.primary,
                marginBottom: 1,
              }}
            >
              Óbito Registrado!
            </Typography>

            {/* Subtítulo */}
            <Typography
              variant="body1"
              sx={{
                color: DESIGN_TOKENS.colors.text.secondary,
                marginBottom: 3,
              }}
            >
              O óbito foi registrado com sucesso
            </Typography>

            {/* Informações do óbito */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                marginBottom: 3,
                padding: 2,
                backgroundColor: DESIGN_TOKENS.colors.primary[50],
                borderRadius: 2,
                border: `1px solid ${DESIGN_TOKENS.colors.primary[100]}`,
              }}
            >
              {/* Nome do animal */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <img 
                  src="/vaca.svg" 
                  alt="Animal" 
                  style={{ 
                    width: 20, 
                    height: 20, 
                    filter: `brightness(0) saturate(100%) invert(20%) sepia(77%) saturate(1200%) hue-rotate(180deg) brightness(95%) contrast(101%)` 
                  }} 
                />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: DESIGN_TOKENS.colors.primary[700],
                  }}
                >
                  {animalName}
                </Typography>
              </Box>
            </Box>

            {/* Mensagem de resumo */}
            <Typography
              variant="body1"
              sx={{
                color: DESIGN_TOKENS.colors.text.primary,
                fontWeight: 600,
                marginBottom: 2,
              }}
            >
              {resumoStr}
            </Typography>

            {/* Mensagem final */}
            <Typography
              variant="body2"
              sx={{
                color: DESIGN_TOKENS.colors.text.secondary,
                fontStyle: 'italic',
              }}
            >
              Operação concluída com sucesso.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    </Fade>
  );
};

export default DeathFeedbackView;